import { TellescopeModelFromName, useAuth } from "@tellescope/tanstack-query";
import { formatName, getEnvironment } from "@tellescope/utils";
import { ReactNode, useEffect, useRef } from "react";
import { User } from "@tellescope/types-models";
import { pendo } from "../tracking";

const initializePendo = (
  user: User,
  selectedOrganization: TellescopeModelFromName<"organizations">,
  onSuccess: ({ userId, orgId }: { userId: string; orgId: string }) => void
) => {
  const environment = getEnvironment();

  try {
    pendo.initialize({
      visitor: {
        id: user.id,
        name: formatName(user),
        isTellescopeEmployee: user.email.includes("@tellescope.com"),
        environment,
      },
      account: {
        id: selectedOrganization.id,
        name: selectedOrganization.name,
        environment,
      },
    });
    onSuccess({ userId: user.id, orgId: selectedOrganization.id });
  } catch {
    console.error("Error initializing Pendo");
  }
};

export interface PendoProviderProps {
  children: ReactNode;
}

export const PendoProvider = ({ children }: PendoProviderProps) => {
  const { selectedOrganization, currentUser } = useAuth();
  const hasInitializedRef = useRef(false);
  const initializedUserIdRef = useRef<string>(undefined);
  const initializedOrgIdRef = useRef<string>(undefined);

  useEffect(() => {
    const canInitialize = selectedOrganization?.id && currentUser.id;
    if (!canInitialize) return;

    const onSuccess = ({ orgId, userId }: { orgId: string; userId: string }) => {
      hasInitializedRef.current = true;
      initializedOrgIdRef.current = orgId;
      initializedUserIdRef.current = userId;
    };

    if (!hasInitializedRef?.current) {
      initializePendo(currentUser, selectedOrganization, onSuccess);
      return;
    }

    const hasChangedUserOrOrg =
      initializedOrgIdRef.current !== selectedOrganization?.id || initializedUserIdRef.current !== currentUser.id;
    if (hasChangedUserOrOrg) {
      initializePendo(currentUser, selectedOrganization, onSuccess);
      return;
    }
  }, [currentUser, selectedOrganization]);

  return children;
};
